import type { TlsRptSummaryResponse } from "@rua-watch/shared";
import { DnsRecordBox } from "./DnsRecordBox";
import { useActiveTenant, useDomainTlsRpt } from "../api/hooks";
import { formatInt, formatPct, fromUnix } from "../lib/format";

// F10 — TLS-RPT (RFC 8460) summary on /domains/:domain. Reports
// aggregated outbound TLS handshake outcomes from receiving MTAs.
// Volume is normally an order of magnitude smaller than DMARC `rua`,
// and many domains never publish a `_smtp._tls` policy at all.
//
// The card used to hide itself when there was no data, which made the
// whole feature invisible to anyone who hadn't already published the
// record — including the part that tells them how. Now the no-data case
// renders the record to publish instead, mirroring the Settings page.

export function TlsRptCard({ domain }: { domain: string }) {
  const q = useDomainTlsRpt(domain);
  if (q.isLoading || !q.data) return null;
  const data = q.data;
  if (!data.hasData) return <TlsRptSetupCard domain={domain} />;

  const totalSessions = data.totals.success + data.totals.failure;
  const failureRate = totalSessions > 0 ? data.totals.failure / totalSessions : 0;
  const headline = headlineFor(failureRate);

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-2">
        <div>
          <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300">
            TLS handshake outcomes
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            RFC 8460 reports aggregated over the last 30 days.
            {data.latestReport ? (
              <>
                {" "}Last received {fromUnix(data.latestReport.receivedAt)} from{" "}
                <span className="font-medium">{data.latestReport.orgName}</span>.
              </>
            ) : null}
          </p>
        </div>
        <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${headline.pillClass}`}>
          {headline.label}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-px bg-slate-100 dark:bg-slate-800 sm:grid-cols-3">
        <Stat
          label="Successful sessions"
          value={formatInt(data.totals.success)}
        />
        <Stat
          label="Failed sessions"
          value={formatInt(data.totals.failure)}
          tone={data.totals.failure > 0 ? "amber" : undefined}
        />
        <Stat
          label="Failure rate"
          value={totalSessions > 0 ? formatPct(failureRate) : "—"}
          tone={failureRate >= 0.05 ? "red" : failureRate > 0 ? "amber" : undefined}
        />
      </div>

      {data.failureBreakdown.length > 0 ? (
        <div className="border-t border-slate-200 dark:border-slate-800 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Top failure types (30d)
          </div>
          <ul className="mt-2 space-y-1">
            {data.failureBreakdown.map((row) => (
              <li key={row.resultType} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate font-mono text-slate-800 dark:text-slate-200">
                  {row.resultType}
                </span>
                <span className="shrink-0 tabular-nums text-slate-600 dark:text-slate-400">
                  {formatInt(row.failedSessionCount)} sessions
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// No TLS-RPT report has ever arrived for this domain. Two reasons in
// practice: the `_smtp._tls` record isn't published, or it is but no
// receiver has sent a report yet (they batch daily). We can't tell the
// two apart from report data alone, so the copy covers both and hands
// over the record either way.
function TlsRptSetupCard({ domain }: { domain: string }) {
  const tenant = useActiveTenant();
  const mailbox = tenant.data?.mailboxAddress;

  return (
    <section className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-2">
        <div>
          <h2 className="text-sm font-medium text-slate-700 dark:text-slate-300">
            TLS handshake outcomes
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            RFC 8460 reports — how receiving mail servers' TLS handshakes with your senders went.
          </p>
        </div>
        <span className="shrink-0 rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          No reports yet
        </span>
      </div>

      <div className="px-4 py-4">
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Nothing has arrived for <span className="font-mono">{domain}</span> yet. Publish the
          record below and receivers start reporting expired certificates, failed STARTTLS and
          MTA-STS mismatches here. It's optional and independent of DMARC.
        </p>
        {mailbox ? (
          <DnsRecordBox
            name={`_smtp._tls.${domain}`}
            type="TXT"
            value={`v=TLSRPTv1; rua=mailto:${mailbox}`}
            copyLabel="Copy record"
          />
        ) : (
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Loading your workspace mailbox address…
          </p>
        )}
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Same mailbox as your DMARC <code>rua=</code>. Reporters batch daily, so the first
          report typically lands the day after you publish.
        </p>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "amber" | "red";
}) {
  const valueColor =
    tone === "red"
      ? "text-red-700 dark:text-red-300"
      : tone === "amber"
      ? "text-amber-700 dark:text-amber-300"
      : "text-slate-900 dark:text-slate-100";
  return (
    <div className="bg-white dark:bg-slate-900 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${valueColor}`}>{value}</div>
    </div>
  );
}

function headlineFor(failureRate: number): { label: string; pillClass: string } {
  if (failureRate >= 0.05) {
    return {
      label: "TLS issues",
      pillClass: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    };
  }
  if (failureRate > 0) {
    return {
      label: "Some failures",
      pillClass: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    };
  }
  return {
    label: "Healthy",
    pillClass: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  };
}

// Re-export for parents that want a simpler integration check.
export type { TlsRptSummaryResponse };
